import { useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Popover, ActionList, Icon } from '@shopify/polaris';
import { ChevronDownIcon } from '@shopify/polaris-icons';
import { useShortlistStore } from '../../store/shortlistStore';
import { useInquiryStore } from '../../store/inquiryStore';

interface NavBarProps {
  isSticky: boolean;
}

export function NavBar({ isSticky }: NavBarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [productsDropdownOpen, setProductsDropdownOpen] = useState(false);

  const shortlistCount = useShortlistStore((s) => s.getCount());
  const inquiryBasketCount = useInquiryStore((s) => s.getItemCount());

  const handleNav = useCallback(
    (path: string) => {
      navigate(path);
      setProductsDropdownOpen(false);
    },
    [navigate]
  );

  const isActive = (path: string, startsWith = false) => {
    if (startsWith) return location.pathname.startsWith(path);
    return location.pathname === path;
  };

  const productsActivator = (
    <button
      className={`nav-link ${isActive('/categories', true) || isActive('/products', true) ? 'nav-link--active' : ''}`}
      onClick={() => setProductsDropdownOpen((o) => !o)}
    >
      Products
      <Icon source={ChevronDownIcon} />
    </button>
  );

  return (
    <nav className={`nav-bar ${isSticky ? 'nav-bar--sticky' : ''}`}>
      <div className="nav-container">
        <div className="nav-links">
          <button
            className={`nav-link ${isActive('/') ? 'nav-link--active' : ''}`}
            onClick={() => handleNav('/')}
          >
            Home
          </button>

          <Popover
            active={productsDropdownOpen}
            activator={productsActivator}
            onClose={() => setProductsDropdownOpen(false)}
            preferredAlignment="left"
          >
            <ActionList
              items={[
                {
                  content: 'All Categories',
                  onAction: () => handleNav('/categories'),
                },
                {
                  content: 'Hot Selling',
                  onAction: () => handleNav('/search?q=hot'),
                },
              ]}
            />
          </Popover>

          <button
            className={`nav-link ${isActive('/catalogue') ? 'nav-link--active' : ''}`}
            onClick={() => handleNav('/catalogue')}
          >
            Catalogue
          </button>

          <button
            className={`nav-link ${isActive('/about') ? 'nav-link--active' : ''}`}
            onClick={() => handleNav('/about')}
          >
            About Us
          </button>

          <button
            className={`nav-link ${isActive('/faq') ? 'nav-link--active' : ''}`}
            onClick={() => handleNav('/faq')}
          >
            FAQ
          </button>

          <button
            className={`nav-link ${isActive('/contact') ? 'nav-link--active' : ''}`}
            onClick={() => handleNav('/contact')}
          >
            Contact Us
          </button>
        </div>

        {/* Right side: activity links (desktop only) */}
        <div className="nav-right">
          <button
            className={`nav-link nav-link--subtle ${isActive('/shortlist') ? 'nav-link--active' : ''}`}
            onClick={() => handleNav('/shortlist')}
          >
            Shortlist{shortlistCount > 0 ? ` (${shortlistCount})` : ''}
          </button>
          <button
            className={`nav-link nav-link--subtle ${isActive('/inquiry-basket') ? 'nav-link--active' : ''}`}
            onClick={() => handleNav('/inquiry-basket')}
          >
            Inquiry Basket{inquiryBasketCount > 0 ? ` (${inquiryBasketCount})` : ''}
          </button>
        </div>
      </div>
    </nav>
  );
}
